import { Component, Input, OnInit, OnDestroy, NgZone } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { SettingsService } from '../../services/settings.service';
import { DataManagementService } from 'src/app/common/services/data-management.service';
import { environment } from 'src/environments/environment';
import { DataService } from 'src/app/services/data.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GamedataService } from 'src/app/build-gamedata/services/gamedata.service';

@Component({
  selector: 'app-data-management',
  templateUrl: './data-management.component.html',
  styleUrls: ['./data-management.component.sass'],
  providers: [ConfirmationService]
})
export class DataManagementComponent implements OnInit, OnDestroy {
  @Input() cmsSettings: any; 
  get autoProdPushEnabled(): boolean {
    return this.cmsSettings?.autoProdPushEnabled;
  }
  set autoProdPushEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.autoProdPushEnabled = value;
    }
  }

  get manualProdPushButtonEnabled(): boolean {
    return this.cmsSettings?.manualProdPushButtonEnabled;
  }
  set manualProdPushButtonEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.manualProdPushButtonEnabled = value;
    }
  }

  get autoDevQAPushEnabled(): boolean {
    return this.cmsSettings?.autoDevQAPushEnabled;
  }
  set autoDevQAPushEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.autoDevQAPushEnabled = value;
    }
  }

  get manualDevQAGamedataPushEnabled(): boolean {
    return this.cmsSettings?.manualDevQAGamedataPushEnabled;
  }
  set manualDevQAGamedataPushEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.manualDevQAGamedataPushEnabled = value;
    }
  }

  get manualDevGamedataPushEnabled(): boolean {
    return this.cmsSettings?.manualDevGamedataPushEnabled;
  }
  set manualDevGamedataPushEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.manualDevGamedataPushEnabled = value;
    }
  }

  get manualQAGamedataPushEnabled(): boolean {
    return this.cmsSettings?.manualQAGamedataPushEnabled;
  }
  set manualQAGamedataPushEnabled(value: boolean) {
    if (this.cmsSettings) {
      this.cmsSettings.manualQAGamedataPushEnabled = value;
    }
  }

  get selectedDefaultPerk(): any {
    return this.cmsSettings?.defaultPerk;
  }
  
  set selectedDefaultPerk(value: any) {
    if (this.cmsSettings) {
      this.cmsSettings.defaultPerk = value;
    }
  }

  isProd: boolean = false;
  env: string;
  perks: any[] = [];
  selectedDefaultPerks: any[] = [];
  private previousDefaultPerks: any[] = [];
  private defaultPerksSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  isResetting: boolean = false;

  isSyncingDB: boolean = false;
  syncStatus: any = null;
  private syncPollInterval: any = null;

  isSyncingLocalizationDB: boolean = false;
  localizationSyncStatus: any = null;
  private localizationSyncPollInterval: any = null;

  constructor(
    private messageService: MessageService,
    private settingsService: SettingsService,
    private dataManagementService: DataManagementService,
    private dataService: DataService,
    private gamedataService: GamedataService,
    private confirmationService: ConfirmationService,
    private ngZone: NgZone
  ) {
    this.defaultPerksSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.handleDefaultPerksUpdate();
      });
  }

  ngOnInit(): void {
    this.setEnv();
    this.loadPerks();
    this.loadDefaultPerks();
    this.loadSyncStatus();
    this.loadLocalizationSyncStatus();
  }

  setEnv() {
    this.env = window.location.href.includes('dev.cms')
      ? 'dev'
      : window.location.href.includes('localhost')
      ? 'local'
      : window.location.href.includes('test.cms')
      ? 'test'
      : 'prod';

    this.isProd = environment.production && this.env === 'prod';
  }

  async handleProdPushChange(event: any, isAutoPromoteSetting: boolean = false) {
    if (isAutoPromoteSetting) {
      await this.settingsService.updateSetting(this.cmsSettings.id, {
        autoProdPushEnabled: this.autoProdPushEnabled,
      });
    } else {
      await this.settingsService.updateSetting(this.cmsSettings.id, {
        manualProdPushButtonEnabled: this.manualProdPushButtonEnabled,
      });
    }
  }

  async handleAutoDevQAPushEnabledChange(event: any) {
    await this.settingsService.updateSetting(this.cmsSettings.id, {
      autoDevQAPushEnabled: this.autoDevQAPushEnabled,
    });
  }

  async handleManualDevQAGamedataPushEnabledChange(event: any) {
    await this.settingsService.updateSetting(this.cmsSettings.id, {
      manualDevQAGamedataPushEnabled: this.manualDevQAGamedataPushEnabled,
    });
  }

  async handleManualDevGamedataPushEnabledChange(event: any) {
    await this.settingsService.updateSetting(this.cmsSettings.id, {
      manualDevGamedataPushEnabled: this.manualDevGamedataPushEnabled,
    });
  }

  async handleManualQAGamedataPushEnabledChange(event: any) {
    await this.settingsService.updateSetting(this.cmsSettings.id, {
      manualQAGamedataPushEnabled: this.manualQAGamedataPushEnabled,
    });
  }

  async loadSyncStatus() {
    try {
      this.syncStatus = await this.dataManagementService.getSyncStatus();
      this.isSyncingDB = this.syncStatus?.inProgress === true;
      if (this.isSyncingDB) {
        this.startSyncPolling();
      }
    } catch (e) {
      // ignore
    }
  }

  private startSyncPolling() {
    if (this.syncPollInterval) return;
    this.ngZone.runOutsideAngular(() => {
      this.syncPollInterval = setInterval(async () => {
        try {
          const status = await this.dataManagementService.getSyncStatus();
          this.ngZone.run(() => {
            this.syncStatus = status;
            if (!status?.inProgress) {
              this.isSyncingDB = false;
              this.stopSyncPolling();
              if (status?.lastStatus === 'success') {
                this.messageService.add({ severity: 'success', summary: 'DB Sync Complete', detail: status.lastMessage || 'Database synced successfully.' });
              } else if (status?.lastStatus === 'failed') {
                this.messageService.add({ severity: 'error', summary: 'DB Sync Failed', detail: status.lastMessage || 'Sync failed.' });
              }
            }
          });
        } catch (e) { /* ignore poll errors */ }
      }, 5000);
    });
  }

  private stopSyncPolling() {
    if (this.syncPollInterval) {
      clearInterval(this.syncPollInterval);
      this.syncPollInterval = null;
    }
  }

  async syncDB() {
    this.isSyncingDB = true;
    try {
      const result = await this.dataManagementService.syncDatabase(this.env);
      if (result?.warning) {
        this.messageService.add({ severity: 'warn', summary: 'DB Sync', detail: result.warning });
        this.isSyncingDB = false;
        return;
      }
      if (result?.error) {
        this.messageService.add({ severity: 'error', summary: 'DB Sync Failed', detail: result.error });
        this.isSyncingDB = false;
        return;
      }
      this.messageService.add({ severity: 'info', summary: 'DB Sync Started', detail: 'Sync in progress. You will be notified on completion.' });
      await this.loadSyncStatus();
      this.startSyncPolling();
    } catch (e: any) {
      this.isSyncingDB = false;
      this.messageService.add({ severity: 'error', summary: 'DB Sync Failed', detail: e?.error?.error || 'Failed to start DB sync.' });
    }
  }

  async loadLocalizationSyncStatus() {
    try {
      this.localizationSyncStatus = await this.dataManagementService.getLocalizationSyncStatus();
      this.isSyncingLocalizationDB = this.localizationSyncStatus?.inProgress === true;
      if (this.isSyncingLocalizationDB) {
        this.startLocalizationSyncPolling();
      }
    } catch (e) {
      // ignore
    }
  }

  private startLocalizationSyncPolling() {
    if (this.localizationSyncPollInterval) return;
    this.ngZone.runOutsideAngular(() => {
      this.localizationSyncPollInterval = setInterval(async () => {
        try {
          const status = await this.dataManagementService.getLocalizationSyncStatus();
          this.ngZone.run(() => {
            this.localizationSyncStatus = status;
            if (!status?.inProgress) {
              this.isSyncingLocalizationDB = false;
              this.stopLocalizationSyncPolling();
              if (status?.lastStatus === 'success') {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Localization Sync Complete',
                  detail: status.lastMessage || 'Localization database synced from production.',
                });
              } else if (status?.lastStatus === 'failed') {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Localization Sync Failed',
                  detail: status.lastMessage || 'Localization sync failed.',
                });
              }
            }
          });
        } catch (e) { /* ignore poll errors */ }
      }, 5000);
    });
  }

  private stopLocalizationSyncPolling() {
    if (this.localizationSyncPollInterval) {
      clearInterval(this.localizationSyncPollInterval);
      this.localizationSyncPollInterval = null;
    }
  }

  syncLocalizationDB() {
    this.confirmationService.confirm({
      message: 'This will replace all localization MySQL data in this environment with production data. Existing translations in this environment will be overwritten. Continue?',
      header: 'Sync Localization Database',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        void this.performLocalizationSync();
      },
    });
  }

  private async performLocalizationSync() {
    this.isSyncingLocalizationDB = true;
    try {
      const result = await this.dataManagementService.syncLocalizationDatabase(this.env);
      if (result?.warning) {
        this.messageService.add({ severity: 'warn', summary: 'Localization Sync', detail: result.warning });
        this.isSyncingLocalizationDB = false;
        return;
      }
      if (result?.error) {
        this.messageService.add({ severity: 'error', summary: 'Localization Sync Failed', detail: result.error });
        this.isSyncingLocalizationDB = false;
        return;
      }
      this.messageService.add({
        severity: 'info',
        summary: 'Localization Sync Started',
        detail: 'Sync in progress. You will be notified on completion, and via Slack.',
      });
      await this.loadLocalizationSyncStatus();
      this.startLocalizationSyncPolling();
    } catch (e: any) {
      this.isSyncingLocalizationDB = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Localization Sync Failed',
        detail: e?.error?.error || e?.error?.message || 'Failed to start localization sync.',
      });
    }
  }

  loadPerks() {
    this.dataService.getAllOfType('perks', {
      query: { defaultPerk: true }, 
      select: '_id id name'
    }).subscribe(
      (perks) => {
        this.perks = perks;
      }
    );
  }

  async loadDefaultPerks() {
    try {
      const response = await this.dataService.getAllOfTypeAsync('cms-settings', {
        query: { id: 1000 },
        select: 'defaultPerk',
        autopopulate: true
      });

      if (response[0]?.defaultPerk) {
        this.selectedDefaultPerks = response[0].defaultPerk;
        this.previousDefaultPerks = [...response[0].defaultPerk];
      }
    } catch (error) {
      console.error('Error loading default perks:', error);
    }
  }

  onDefaultPerksChange(event: any) {
    this.defaultPerksSubject.next();
  }

  private async handleDefaultPerksUpdate() {
    console.log(this.selectedDefaultPerks);
    const settings = {
      defaultPerk: this.selectedDefaultPerks
    };

    const newIds = this.selectedDefaultPerks.map((perk: any) => perk.id);
    const previousIds = this.previousDefaultPerks.map((perk: any) => perk.id);
    
    console.log('New IDs:', newIds, 'Previous IDs:', previousIds);
    
    // Find removed and added perk ids
    const removedIds = previousIds.filter(id => !newIds.includes(id));
    const addedIds = newIds.filter(id => !previousIds.includes(id));

    try {
      await this.dataService.updateRecord(1000, 'cms-settings', settings);
      
      // Update only newly added perks to true
      for (const id of addedIds) {
        await this.dataService.updateRecord(id, 'perks', { isSelectedDefaultPerk: true });
      }
      
      // Update only removed perks to false
      for (const id of removedIds) {
        await this.dataService.updateRecord(id, 'perks', { isSelectedDefaultPerk: false });
      }

      // Update previous selection
      this.previousDefaultPerks = [...this.selectedDefaultPerks];
    
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Default perks updated successfully'
      });
    } catch (error) {
      console.error('Error updating default perks:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update default perks'
      });
    }
  }

  resetGamedataSystem(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to reset the build system? This will:\n' +
               '• Cancel all active jobs\n' +
               '• Clear all queued jobs\n' +
               '• Reset build locks\n' +
               '• Clear build cache\n\n' +
               'This action cannot be undone.',
      header: 'Reset Build System',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await this.performSystemReset();
      }
    });
  }

  private async performSystemReset(): Promise<void> {
    this.isResetting = true;
    
    try {
      const response = await this.gamedataService.resetSystem();
      
      if (response.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'System Reset Complete',
          detail: response.message || 'Build system has been reset successfully.'
        });
      } else if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Reset Failed',
          detail: response.error
        });
      }
    } catch (error: any) {
      const errorMessage = error.status === 403 
        ? (error.error?.error || error.statusText) 
        : 'An error occurred while resetting the system.';
      
      this.messageService.add({
        severity: 'error',
        summary: 'Error Resetting System',
        detail: errorMessage
      });
    } finally {
      this.isResetting = false;
    }
  }

  ngOnDestroy() {
    this.stopSyncPolling();
    this.stopLocalizationSyncPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
